import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { getStrokeColor } from "./helpers";
import {
    CapsuleRiskScoresColors,
    CapsuleRiskScoresStrokeWidth,
    QuadrantFillPaths,
    QuadrantStrokePaths,
} from "../constants";
import { PROSTATE_QUADRANT } from "../../__shared__/Viewers/consts";

// =====================================================================================================================
// COORDINATE SYSTEM CONSTANTS
// All values are determined through trial and error for optimal visual layout
// =====================================================================================================================

// Capsule Risk Score coordinates - where the numeric scores appear inside quadrants
const CRM_SCORE_OFFSET_LEFT_PCT = 0.33;
const CRM_SCORE_OFFSET_RIGHT_PCT = 0.62;
const CRM_SCORE_OFFSET_TOP_PCT = 0.4;
const CRM_SCORE_OFFSET_BOTTOM_PCT = 0.72;

// Position label coordinates - where the "Right"/"Left", "Anterior", "Posterior" labels appear.
const CRM_POSITION_LABEL_X_START_PCT = 0.1;
const CRM_POSITION_LABEL_X_END_PCT = 0.85;
const CRM_POSITION_LABEL_X_CENTER_PCT = 0.45;
const CRM_POSITION_LABEL_Y_START_PCT = 0.08;
const CRM_POSITION_LABEL_Y_END_PCT = 0.98;
const CRM_POSITION_LABEL_Y_CENTER_PCT = 0.52;

// The labels shift to fit the surgery mode layout
// The Anterior/Posterior X offsets are named for the label they position, not for a side
const CRM_POSITION_LABEL_X_START_PCT_SURGERY_MODE = 0.07;
const CRM_POSITION_LABEL_Y_START_PCT_SURGERY_MODE = 0.05;
const CRM_POSITION_LABEL_Y_END_PCT_SURGERY_MODE = 0.97;
const CRM_POSITION_LABEL_ANTERIOR_X_PCT_SURGERY_MODE = 0.44;
const CRM_POSITION_LABEL_POSTERIOR_X_PCT_SURGERY_MODE = 0.43;

// SVG boundary coordinates
const CRM_SVG_RADIUS_X = 0.4;
const CRM_SVG_RADIUS_Y = 0.35;
const CRM_SVG_WIDTH_PX = 101;
const CRM_SVG_HEIGHT_PX = 71.98;

// Quadrant divider line positions
const CRM_SVG_DIVIDER_X_START = 0.265;
const CRM_SVG_DIVIDER_X_END = 0.735;
const CRM_SVG_DIVIDER_Y_START = 0.135;
const CRM_SVG_DIVIDER_Y_END = 0.82;

// In surgery mode the Left/Right axis is flipped
const FLIPPED_QUADRANT_KEY = {
    RA: "LA",
    LA: "RA",
    RP: "LP",
    LP: "RP",
};

/*
 * CRMDiagram Component
 *
 * Renders a canvas diagram showing prostate quadrant-specific risk scores.
 * The diagram displays an axial view of the prostate divided into 4 quadrants:
 * - RA: Right Anterior
 * - LA: Left Anterior
 * - RP: Right Posterior
 * - LP: Left Posterior
 *
 * Each quadrant shows a risk score and is color-coded based on its ECE risk level.
 * Users can hover over a quadrant to highlight it. Hovering will trigger a rectangle overlay in the 2D MRI viewer.
 */
function CRMDiagram(props) {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [size, setSize] = useState({ width: 0, height: 0 });
    const [hoveredQuadrant, setHoveredQuadrant] = useState(
        PROSTATE_QUADRANT.NONE
    );

    // The Left/Right axis is flipped when not in surgery mode AXIAL view
    function getQuadrantKey(slotKey) {
        return props.flipLeftRight ? FLIPPED_QUADRANT_KEY[slotKey] : slotKey;
    }

    // Resize handler
    useEffect(() => {
        function resize() {
            if (containerRef.current) {
                const { clientWidth, clientHeight } = containerRef.current;
                setSize({ width: clientWidth, height: clientHeight });
            }
        }

        resize();
        window.addEventListener("resize", resize);
        return () => {
            window.removeEventListener("resize", resize);
        };
    }, []);

    // Updates canvas resolution for proper rendering:
    // Resize canvas pixel buffer on size change to update the internal resolution of the canvas,
    // so that rendering looks sharp and scales properly across screen sizes and pixel densities.
    // Canvas pixel buffer is the internal area the browser uses to render pixels on the canvas,
    // defined by the canvas's width and height attributes, not the CSS.
    // Example:
    //   width=600, height=400 → internal pixel buffer (used for rendering)
    //   style="width: 300px; height: 200px" → how it’s displayed on screen
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || size.width === 0 || size.height === 0) return;

        const dpr = window.devicePixelRatio || 1;
        canvas.width = size.width * dpr;
        canvas.height = size.height * dpr;
        canvas.style.width = `${size.width}px`;
        canvas.style.height = `${size.height}px`;

        const context = canvasRef.current.getContext("2d");

        if (!context) {
            return;
        }

        context.setTransform(dpr, 0, 0, dpr, 0, 0);

        drawWhenFontsLoad(context, hoveredQuadrant);
    }, [size]);

    // Redraw canvas when the hover state changes
    useEffect(() => {
        const canvas = canvasRef.current;

        if (!canvas) {
            return;
        }

        const context = canvas.getContext("2d");
        if (context) {
            drawWhenFontsLoad(context, hoveredQuadrant);
        }
    }, [hoveredQuadrant, size, props.flipLeftRight]);

    // Handle mouse interations
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }

        // Determine if a point is inside the prostate (ellipsis shape) using (x/a)^2 + (y/b)^2 <= 1
        function isPointInProstate(x, y) {
            const centerX = size.width / 2;
            const centerY = size.height / 2;
            const radiusX = size.width * CRM_SVG_RADIUS_X;
            const radiusY = size.height * CRM_SVG_RADIUS_Y;

            const normalizedDistance =
                Math.pow((x - centerX) / radiusX, 2) +
                Math.pow((y - centerY) / radiusY, 2);

            return normalizedDistance <= 1;
        }

        function handleMouseMove(event) {
            const rect = canvas.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const y = event.clientY - rect.top;
            const centerX = size.width / 2;
            const centerY = size.height / 2;

            let quadrant;
            if (isPointInProstate(x, y)) {
                const isLeft = x >= centerX; // right vs. left
                const isAnterior = y < centerY; // anterior vs. posterior

                // Map the mouse position to quadrant numbers
                if (isLeft && isAnterior) {
                    quadrant = PROSTATE_QUADRANT.ANTERIOR_LEFT; // 1
                } else if (!isLeft && isAnterior) {
                    quadrant = PROSTATE_QUADRANT.ANTERIOR_RIGHT; // 2
                } else if (isLeft && !isAnterior) {
                    quadrant = PROSTATE_QUADRANT.POSTERIOR_LEFT; // 3
                } else {
                    quadrant = PROSTATE_QUADRANT.POSTERIOR_RIGHT; // 4
                }
            }

            setHoveredQuadrant(quadrant);
        }

        function handleMouseLeave() {
            setHoveredQuadrant(PROSTATE_QUADRANT.NONE);
        }

        canvas.addEventListener("mousemove", handleMouseMove);
        canvas.addEventListener("mouseleave", handleMouseLeave);
        return () => {
            canvas.removeEventListener("mousemove", handleMouseMove);
            canvas.removeEventListener("mouseleave", handleMouseLeave);
        };
    }, [size]);

    // Update the CRMModel when hovering over quadrants
    useEffect(() => {
        if (!props.surgeryMode && hoveredQuadrant) {
            const model = props.patientAnatomyModel.prostate;
            if (model && model.crm) {
                model.crm.quadrant = hoveredQuadrant;
            }
        }
    }, [hoveredQuadrant]);

    // Wait for fonts to load before drawing to prevent text from rendering with the fallback font initially
    function drawWhenFontsLoad(context, hoveredQuadrant) {
        document.fonts.ready.then(() => {
            draw(context, hoveredQuadrant);
        });
    }

    // Main drawing function for the canvas
    function draw(context, hoveredQuadrant) {
        context.clearRect(0, 0, size.width, size.height);

        const centerX = size.width / 2;
        const centerY = size.height / 2;

        // SVG quadrant dimensions (referenced in QuadrantStrokePaths and QuadrantFillPaths)
        // Width: 101px, Height: 71.98px
        // Scale factors: /4 and /2.5, empirically determined values to properly fit
        const scaleX = size.width / CRM_SVG_WIDTH_PX / 4;
        const scaleY = size.height / CRM_SVG_HEIGHT_PX / 2.5;
        const scores = props.quadrantScores || {};

        drawQuadrants(
            context,
            centerX,
            centerY,
            scaleX,
            scaleY,
            scores,
            hoveredQuadrant
        );
        drawOrientationLabels(context);
        drawCapsularRiskScores(context, scores);
        drawQuadrantDividers(context, centerX, centerY);
        if (!props.surgeryMode) {
            drawLegend(context);
        }
    }

    // Draws the four prostate quadrants using SVG paths
    function drawQuadrants(
        context,
        cx,
        cy,
        scaleX,
        scaleY,
        scores,
        hoveredQuadrant
    ) {
        const quadrants = [
            {
                key: "RA", // Right Anterior
                quadrant: PROSTATE_QUADRANT.ANTERIOR_RIGHT,
                dx: -cx * 0.5,
                dy: cy * 0.2,
            },
            {
                key: "LA", // Left Anterior
                quadrant: PROSTATE_QUADRANT.ANTERIOR_LEFT,
                dx: 0,
                dy: cy * 0.2,
            },
            {
                key: "RP", // Right Posterior
                quadrant: PROSTATE_QUADRANT.POSTERIOR_RIGHT,
                dx: -cx * 0.5,
                dy: cy,
            },
            {
                key: "LP", // Left Posterior
                quadrant: PROSTATE_QUADRANT.POSTERIOR_LEFT,
                dx: 0,
                dy: cy,
            },
        ];

        quadrants.forEach(({ key, quadrant, dx, dy }) => {
            context.save();
            context.translate(cx + dx, dy);
            context.scale(scaleX, scaleY);
            drawSvgFillPath(
                context,
                quadrant,
                QuadrantStrokePaths[key],
                QuadrantFillPaths[key],
                scores[getQuadrantKey(key)] ?? 0,
                hoveredQuadrant
            );
            context.restore();
        });
    }

    function drawSvgFillPath(
        context,
        quadrant,
        strokePathStr,
        fillPathStr,
        score,
        hoveredQuadrant
    ) {
        const strokePath = new Path2D(strokePathStr);
        const fillPath = new Path2D(fillPathStr);

        if (!props.surgeryMode && hoveredQuadrant === quadrant) {
            context.fillStyle = CapsuleRiskScoresColors.HOVER;
            context.fill(fillPath);
        }

        context.strokeStyle = getStrokeColor(score);
        context.lineWidth = CapsuleRiskScoresStrokeWidth.PROSTATE;
        context.stroke(strokePath);
    }

    function drawQuadrantDividers(context, cx, cy) {
        context.strokeStyle = CapsuleRiskScoresColors.DIVIDER_AND_TEXT;
        context.lineWidth = CapsuleRiskScoresStrokeWidth.DIVIDERS;

        // Vertical divider
        context.beginPath();
        context.moveTo(cx, size.height * CRM_SVG_DIVIDER_Y_START);
        context.lineTo(cx, size.height * CRM_SVG_DIVIDER_Y_END);
        context.stroke();

        // Horizontal divider
        context.beginPath();
        context.moveTo(size.width * CRM_SVG_DIVIDER_X_START, cy);
        context.lineTo(size.width * CRM_SVG_DIVIDER_X_END, cy);
        context.stroke();
    }

    function drawCapsularRiskScores(context, scores) {
        if (!scores) {
            return;
        }

        // Calculate score positions based on canvas size
        const leftX = size.width * CRM_SCORE_OFFSET_LEFT_PCT;
        const topY = size.height * CRM_SCORE_OFFSET_TOP_PCT;
        const rightX = size.width * CRM_SCORE_OFFSET_RIGHT_PCT;
        const bottomY = size.height * CRM_SCORE_OFFSET_BOTTOM_PCT;

        context.font = "500 20px 'Roboto'";
        context.fillStyle = CapsuleRiskScoresColors.DIVIDER_AND_TEXT;

        context.fillText(`${scores[getQuadrantKey("RA")] ?? 0}`, leftX, topY);
        context.fillText(`${scores[getQuadrantKey("LA")] ?? 0}`, rightX, topY);
        context.fillText(
            `${scores[getQuadrantKey("RP")] ?? 0}`,
            leftX,
            bottomY
        );
        context.fillText(
            `${scores[getQuadrantKey("LP")] ?? 0}`,
            rightX,
            bottomY
        );
    }

    function drawOrientationLabels(context) {
        context.font = "normal 14px 'Roboto'";
        context.fillStyle = CapsuleRiskScoresColors.DIVIDER_AND_TEXT;

        // Canvas offsets only - which anatomical label lands at xStartPct depends on surgery mode
        const xStartPct = props.surgeryMode
            ? CRM_POSITION_LABEL_X_START_PCT_SURGERY_MODE
            : CRM_POSITION_LABEL_X_START_PCT;
        const anteriorXPct = props.surgeryMode
            ? CRM_POSITION_LABEL_ANTERIOR_X_PCT_SURGERY_MODE
            : CRM_POSITION_LABEL_X_CENTER_PCT;
        const anteriorYPct = props.surgeryMode
            ? CRM_POSITION_LABEL_Y_START_PCT_SURGERY_MODE
            : CRM_POSITION_LABEL_Y_START_PCT;
        const posteriorXPct = props.surgeryMode
            ? CRM_POSITION_LABEL_POSTERIOR_X_PCT_SURGERY_MODE
            : CRM_POSITION_LABEL_X_CENTER_PCT;
        const posteriorYPct = props.surgeryMode
            ? CRM_POSITION_LABEL_Y_END_PCT_SURGERY_MODE
            : CRM_POSITION_LABEL_Y_END_PCT;

        // Left/Right text flip sides when the axes are flipped
        context.fillText(
            props.flipLeftRight ? "Left" : "Right",
            size.width * xStartPct,
            size.height * CRM_POSITION_LABEL_Y_CENTER_PCT
        );
        context.fillText(
            props.flipLeftRight ? "Right" : "Left",
            size.width * CRM_POSITION_LABEL_X_END_PCT,
            size.height * CRM_POSITION_LABEL_Y_CENTER_PCT
        );

        // Top / Bottom labels
        context.fillText(
            "Anterior",
            size.width * anteriorXPct,
            size.height * anteriorYPct
        );
        context.fillText(
            "Posterior",
            size.width * posteriorXPct,
            size.height * posteriorYPct
        );

        if (!props.surgeryMode) {
            context.fillText(
                "Axial View",
                size.width * CRM_POSITION_LABEL_X_END_PCT,
                size.height * CRM_POSITION_LABEL_Y_END_PCT
            );
        }
    }

    // Draws the risk level legend in bottom-left corner
    function drawLegend(context) {
        const startingX = 15;
        const startingY = size.height - 75; // Position from bottom of canvas
        const squareSize = 15;
        const padding = 7;

        drawLegendBackground(context, startingX, startingY);
        drawLegendSquare(context, startingX, startingY, squareSize, padding);
        drawLegendKeys(context, startingX, startingY, squareSize, padding);
    }

    // Draws the rectangle background with border radius for the legend
    // The arc() method is used to create rounded corners, because there isn't a built-in API to draw rectangles with radii
    function drawLegendBackground(context, startingX, startingY) {
        const radius = 2;
        const rectangleWidth = 85;
        const rectangleHeight = 73;

        context.fillStyle = CapsuleRiskScoresColors.BG;
        context.strokeStyle = CapsuleRiskScoresColors.BG;

        // Drawing order
        // 1. Start at the top left corner after the radius
        // 2. Draw the top edge
        // 3. Draw the top right corner
        // 4. Draw the right edge
        // 5. Draw the bottom right corner
        // 6. Draw the bottom edge
        // 7. Draw the bottom left corner
        // 8. Draw the left edge
        // 9. Draw the top left corner
        context.beginPath();
        context.moveTo(startingX + radius, startingY);
        context.lineTo(startingX + rectangleWidth - radius, startingY);
        context.arc(
            startingX + rectangleWidth - radius,
            startingY + radius,
            radius,
            -Math.PI / 2,
            0
        );
        context.lineTo(
            startingX + rectangleWidth,
            startingY + rectangleHeight - radius
        );
        context.arc(
            startingX + rectangleWidth - radius,
            startingY + rectangleHeight - radius,
            radius,
            0,
            Math.PI / 2
        );
        context.lineTo(startingX + radius, startingY + rectangleHeight);
        context.arc(
            startingX + radius,
            startingY + rectangleHeight - radius,
            radius,
            Math.PI / 2,
            Math.PI
        );
        context.lineTo(startingX, startingY + radius);
        context.arc(
            startingX + radius,
            startingY + radius,
            radius,
            Math.PI,
            Math.PI * 1.5
        );
        context.closePath();
        context.fill();
        context.stroke();
    }

    // Draws color-coded squares for the legend
    function drawLegendSquare(
        context,
        startingX,
        startingY,
        squareSize,
        padding
    ) {
        // High ECE risk
        context.fillStyle = CapsuleRiskScoresColors.PROSTATE_HIGH;
        context.fillRect(
            startingX + padding,
            startingY + padding,
            squareSize,
            squareSize
        );

        // Medium ECE risk
        context.fillStyle = CapsuleRiskScoresColors.PROSTATE_MID;
        context.fillRect(
            startingX + padding,
            startingY + squareSize + padding * 2,
            squareSize,
            squareSize
        );

        // Low ECE risk
        context.fillStyle = CapsuleRiskScoresColors.PROSTATE_LOW;
        context.fillRect(
            startingX + padding,
            startingY + squareSize * 2 + padding * 3,
            squareSize,
            squareSize
        );
    }

    // Draws text labels for legend risk levels
    function drawLegendKeys(
        context,
        startingX,
        startingY,
        squareSize,
        padding
    ) {
        context.font = "normal 14px 'Roboto'";
        context.fillStyle = CapsuleRiskScoresColors.DIVIDER_AND_TEXT;

        const legendKeyStartPosition = startingX + squareSize + padding * 2.5;
        const textOffsetY = 4; // Vertical alignment adjustment TODO: Is there a better way to achieve alignment?

        // High ECE risk label
        context.fillText(
            "> 18",
            legendKeyStartPosition,
            startingY + squareSize + textOffsetY
        );

        // Medium ECE risk label
        context.fillText(
            "3.5 - 18",
            legendKeyStartPosition,
            startingY + squareSize * 2 + textOffsetY * 3
        );

        // Low ECE risk label
        context.fillText(
            "< 3.5",
            legendKeyStartPosition,
            startingY + squareSize * 3 + textOffsetY * 4.5
        );
    }

    return (
        <div ref={containerRef} className="h-full w-full">
            <canvas ref={canvasRef} id="crm-prostate-cartoon" />
        </div>
    );
}

CRMDiagram.propTypes = {
    quadrantScores: PropTypes.shape({
        RA: PropTypes.number,
        LA: PropTypes.number,
        RP: PropTypes.number,
        LP: PropTypes.number,
    }),
    patientAnatomyModel: PropTypes.object,
    surgeryMode: PropTypes.bool,
    flipLeftRight: PropTypes.bool,
};

export default CRMDiagram;
